/**
 * alpine-init.js — App-wide Alpine stores.
 *
 * Alpine loads from a CDN <script defer> in base.html — Alpine itself
 * auto-starts on DOMContentLoaded. We want our $store.ui registered
 * BEFORE Alpine starts hydrating, otherwise every page-level
 * `$store.ui.modal === '...'` expression throws "Cannot read
 * properties of undefined (reading 'modal')" on first evaluation.
 *
 * Strategy:
 *   - Register a `alpine:init` listener (this is the official hook).
 *   - Also poll for `window.Alpine` because, with both scripts being
 *     `defer`'d in source order, this file evaluates AFTER the Alpine
 *     CDN bundle finishes executing — at which point alpine:init
 *     has already fired and the listener is too late. Polling
 *     catches that race.
 */

/* ── Overlay a11y (S1): focus management + background inert ─────────────
   Shared by modal() (overlay.html) and drawer() (drawer.html) via the
   $store.ui open/close methods below. This state lives in a plain
   module object — NOT on the reactive store — so Alpine never wraps DOM
   nodes (activeElement, inerted elements) in a reactivity proxy, which
   would corrupt focus() identity and inert bookkeeping.

   Both dialogs render INSIDE <main> (inside .app-wrapper), so we can't
   just inert the shell root — that would disable the open dialog too.
   Instead we walk from the dialog up to the shell root and inert every
   OFF-PATH sibling: the dialog and its ancestors stay interactive while
   the whole background becomes inert + aria-hidden. */
const _overlay = { lastFocus: null, inerted: [] };

const _OVERLAY_FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),' +
  'input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function _overlayVisible(el) {
  return !!(el && (el.offsetParent !== null || el.getClientRects().length));
}

function _overlayFind(kind, id) {
  if (kind === 'modal') {
    const esc = window.CSS && CSS.escape ? CSS.escape(id) : id;
    return document.querySelector('[data-overlay-id="' + esc + '"]');
  }
  // drawer: drawer.html is not editable, so match the visible aria-modal
  // dialog that is NOT a modal (modals carry data-overlay-id).
  const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
  for (let i = 0; i < dialogs.length; i++) {
    const d = dialogs[i];
    if (d.hasAttribute('data-overlay-id')) continue;
    if (_overlayVisible(d)) return d;
  }
  return null;
}

function _overlayStashFocus(alreadyOpen) {
  // Only capture the trigger when NO overlay was open — preserves the
  // outermost opener across a modal+drawer stack.
  if (!alreadyOpen) _overlay.lastFocus = document.activeElement;
}

function _overlayRestoreFocus() {
  const el = _overlay.lastFocus;
  _overlay.lastFocus = null;
  if (el && typeof el.focus === 'function') {
    // rAF so the element is interactive again (inert already cleared).
    requestAnimationFrame(() => { try { el.focus(); } catch (e) {} });
  }
}

function _overlayInertEl(el) {
  if (!el || el.nodeType !== 1 || el.__uiInert) return;
  // Never inert another overlay that is currently open (modal+drawer).
  if (el.getAttribute('aria-modal') === 'true' && _overlayVisible(el)) return;
  el.__uiInert = {
    hadInert: el.hasAttribute('inert'),
    hadHidden: el.hasAttribute('aria-hidden'),
    prevHidden: el.getAttribute('aria-hidden'),
  };
  el.setAttribute('inert', '');
  el.setAttribute('aria-hidden', 'true');
  _overlay.inerted.push(el);
}

function _overlayApplyInert(dialog) {
  const root = document.querySelector('.app-wrapper') || document.body;
  let node = dialog;
  while (node && node.parentElement) {
    const parent = node.parentElement;
    const sibs = parent.children;
    for (let i = 0; i < sibs.length; i++) {
      if (sibs[i] !== node) _overlayInertEl(sibs[i]);
    }
    if (parent === root) break;
    node = parent;
  }
}

function _overlayClearInert() {
  const list = _overlay.inerted;
  for (let i = 0; i < list.length; i++) {
    const el = list[i];
    const st = el.__uiInert;
    if (!st || !st.hadInert) el.removeAttribute('inert');
    if (st && st.hadHidden) el.setAttribute('aria-hidden', st.prevHidden);
    else el.removeAttribute('aria-hidden');
    delete el.__uiInert;
  }
  _overlay.inerted = [];
}

function _overlayFocusFirst(dialog) {
  let target = dialog.querySelector('[autofocus]');
  if (!target) {
    const f = dialog.querySelectorAll(_OVERLAY_FOCUSABLE);
    for (let i = 0; i < f.length; i++) {
      if (_overlayVisible(f[i])) { target = f[i]; break; }
    }
  }
  if (!target) {
    // Nothing focusable — focus the panel itself so the trap has an anchor.
    dialog.setAttribute('tabindex', '-1');
    target = dialog;
  }
  try { target.focus(); } catch (e) {}
}

function _overlayActivate(kind, id) {
  const run = () => {
    const dialog = _overlayFind(kind, id);
    if (!dialog) return;
    _overlayApplyInert(dialog);
    _overlayFocusFirst(dialog);
  };
  // Wait for Alpine x-show to flip display, then a frame for layout.
  const tick =
    window.Alpine && window.Alpine.nextTick
      ? window.Alpine.nextTick
      : (cb) => requestAnimationFrame(cb);
  tick(() => requestAnimationFrame(run));
}

const UI_STORE = {
  modal: null,
  drawer: null,
  toasts: [],
  _seq: 0,

  openModal(id) {
    _overlayStashFocus(!!(this.modal || this.drawer));
    this.modal = id;
    document.documentElement.style.overflow = 'hidden';
    _overlayActivate('modal', id);
  },
  closeModal() {
    this.modal = null;
    // Only tear down the shared lock/inert/focus once BOTH overlays closed
    // (avoids double-unlocking when a drawer is still open behind a modal).
    if (!this.drawer) {
      document.documentElement.style.overflow = '';
      _overlayClearInert();
      _overlayRestoreFocus();
    }
    // NOTE: returns undefined — callers rely on this contract.
  },

  openDrawer(id) {
    _overlayStashFocus(!!(this.modal || this.drawer));
    this.drawer = id;
    document.documentElement.style.overflow = 'hidden';
    _overlayActivate('drawer', id);
  },
  closeDrawer() {
    this.drawer = null;
    if (!this.modal) {
      document.documentElement.style.overflow = '';
      _overlayClearInert();
      _overlayRestoreFocus();
    }
  },

  toast(opts, variant, timeout) {
    if (variant === undefined) variant = 'info';
    if (timeout === undefined) timeout = 5000;
    const t = typeof opts === 'string' ? { message: opts } : Object.assign({}, opts);
    const id = ++this._seq;
    this.toasts.push({
      id: id,
      title: t.title || '',
      message: t.message || '',
      variant: t.variant || variant,
    });
    const ttl = t.timeout != null ? t.timeout : timeout;
    if (ttl > 0) setTimeout(() => this.dismissToast(id), ttl);
    return id;
  },
  dismissToast(id) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  },
};

/**
 * commandPalette — Alpine component factory for the global cmd/ctrl-K palette
 * rendered by the `command_palette` Jinja macro (components/navigation.html).
 *
 * `items` are pre-enriched server-side: each has _k (stable key), label, url,
 * action (window fn name), hint, group, keywords, _iconSvg (rendered <i>).
 * The component owns its own open state + a window keydown listener so it
 * needs no global store. Fuzzy match is a lightweight subsequence scorer.
 */
function commandPaletteFactory(items) {
  return {
    open: false,
    query: '',
    cursor: 0,
    _items: Array.isArray(items) ? items : [],

    get filtered() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this._items;
      const scored = [];
      for (const it of this._items) {
        const hay = (it.label + ' ' + (it.group || '') + ' ' + (it.keywords || '')).toLowerCase();
        const s = this._score(hay, q);
        if (s > -Infinity) scored.push({ it: it, s: s });
      }
      scored.sort((a, b) => b.s - a.s);
      return scored.map((x) => x.it);
    },

    // Subsequence match: all chars of q appear in order in hay. Score rewards
    // contiguous runs + word-boundary starts so "dash" ranks Dashboard high.
    _score(hay, q) {
      let hi = 0;
      let score = 0;
      let run = 0;
      for (let qi = 0; qi < q.length; qi++) {
        const c = q[qi];
        const found = hay.indexOf(c, hi);
        if (found === -1) return -Infinity;
        if (found === hi) { run++; score += 2 + run; }
        else { run = 0; score += 1; }
        if (found === 0 || hay[found - 1] === ' ') score += 4;
        hi = found + 1;
      }
      return score;
    },

    show() {
      this.open = true;
      this.query = '';
      this.cursor = 0;
      document.documentElement.style.overflow = 'hidden';
      this.$nextTick(() => {
        this.$refs.input && this.$refs.input.focus();
        if (window.Lucide && this.$refs.list) {
          window.Lucide.createIcons({ icons: window.Lucide.icons, nameAttr: 'data-lucide' });
        }
      });
    },
    hide() {
      this.open = false;
      if (!window.Alpine || !window.Alpine.store('ui') || !window.Alpine.store('ui').modal) {
        document.documentElement.style.overflow = '';
      }
    },
    toggle() { this.open ? this.hide() : this.show(); },

    move(dir) {
      const n = this.filtered.length;
      if (!n) return;
      this.cursor = (this.cursor + dir + n) % n;
      this.$nextTick(() => {
        const el = this.$refs.list && this.$refs.list.querySelector('#cmdk-opt-' + this.cursor);
        el && el.scrollIntoView({ block: 'nearest' });
      });
    },

    run(it) {
      if (!it) return;
      this.hide();
      if (it.action && typeof window[it.action] === 'function') {
        window[it.action]();
      } else if (it.url) {
        window.location.href = it.url;
      }
    },

    onWindowKey(e) {
      // Global cmd/ctrl-K — but never steal it from an active text editor:
      // when focus is in an input / textarea / contenteditable the page's own
      // shortcut (markdown link, etc.) keeps priority.
      const k = (e.key || '').toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        const t = e.target;
        const tag = t && t.tagName;
        const typing =
          tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable);
        if (typing && !this.open) return; // let editors handle their own ⌘K
        e.preventDefault();
        this.toggle();
      }
    },
  };
}

function _registerUiStore() {
  if (!window.Alpine || window.Alpine.__uiStoreRegistered) return false;
  window.Alpine.store('ui', UI_STORE);
  window.Alpine.data('commandPalette', commandPaletteFactory);
  window.Alpine.__uiStoreRegistered = true;
  return true;
}

// 1. Official hook — fires once, before Alpine's auto-start hydrates the DOM.
document.addEventListener('alpine:init', _registerUiStore);

// 2. Synchronous safety net — if Alpine is already present (alpine:init
//    fired before this script ran), register the store immediately.
if (window.Alpine) {
  _registerUiStore();
} else {
  // 3. Short poll for the race where the CDN script is still parsing
  //    when this file evaluates.
  let tries = 0;
  const poll = setInterval(() => {
    if (_registerUiStore() || ++tries > 50) clearInterval(poll);
  }, 25);
}
